/**
 * CandidateListPage — Issue #188.
 *
 * Read-only candidate directory. Email / phone / id-card columns show last4
 * only; full text reveal is out of scope (separate Issue + audit).
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_CANDIDATE_STAGE_LABELS,
  useHrCandidates,
  type HrCandidateDto,
} from '@/hooks/useHrIssue188';

function fmt(value?: string | null): string {
  return value && value.length > 0 ? value : '—';
}

function mask(value: string | null | undefined, label: string): string {
  return value && value.length >= 1 ? `${label}***${value}` : '—';
}

export default function CandidateListPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.CANDIDATE_READ);

  const { items, total, loading, error, fetchCandidates } = useHrCandidates();
  const [stage, setStage] = useState<HrCandidateDto['stage'] | ''>('');
  const [nameFilter, setNameFilter] = useState('');
  const [position, setPosition] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!canRead) return;
    void fetchCandidates({
      stage: stage || undefined,
      nameContains: nameFilter || undefined,
      appliedPosition: position || undefined,
      page,
      limit,
    });
  }, [canRead, stage, nameFilter, position, page, fetchCandidates]);

  if (!canRead) return <AccessDenied resource="候选人列表" />;

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">候选人列表</h1>
          <p className="text-sm text-slate-500">
            手机号 / 邮箱 / 身份证号默认只展示后四位；完整明文需另立 reveal Issue。
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          姓名包含
          <input
            type="text"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={nameFilter}
            onChange={(e) => {
              setNameFilter(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          应聘岗位
          <input
            type="text"
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={position}
            onChange={(e) => {
              setPosition(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          阶段
          <select
            className="rounded border border-slate-300 px-2 py-1 text-sm"
            value={stage}
            onChange={(e) => {
              setStage((e.target.value as HrCandidateDto['stage']) || '');
              setPage(1);
            }}
          >
            <option value="">全部</option>
            {(Object.entries(HR_CANDIDATE_STAGE_LABELS) as [
              HrCandidateDto['stage'],
              string,
            ][]).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-3 py-2 w-32">姓名</th>
                <th className="px-3 py-2 w-32">阶段</th>
                <th className="px-3 py-2 w-32">岗位</th>
                <th className="px-3 py-2 w-32">手机后四位</th>
                <th className="px-3 py-2 w-32">邮箱后四位</th>
                <th className="px-3 py-2 w-32">来源</th>
                <th className="px-3 py-2 w-24 text-right">匹配度</th>
                <th className="px-3 py-2 w-32">操作</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{row.fullName}</td>
                  <td className="px-3 py-2">{HR_CANDIDATE_STAGE_LABELS[row.stage]}</td>
                  <td className="px-3 py-2">{fmt(row.appliedPosition)}</td>
                  <td className="px-3 py-2">{mask(row.phoneLast4, '手机')}</td>
                  <td className="px-3 py-2">{mask(row.emailLast4, '邮箱')}</td>
                  <td className="px-3 py-2">{row.source}</td>
                  <td className="px-3 py-2 text-right">
                    {row.matchScore !== null ? `${row.matchScore}%` : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      href={`/hr/candidates/${row.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      详情 →
                    </Link>
                  </td>
                </tr>
              ))}
              {items.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-slate-400">
                    没有候选人
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>

      <footer className="flex items-center justify-between text-sm text-slate-600">
        <span>
          共 {total} 条 / 第 {page} / {totalPages} 页
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            上一页
          </button>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1 disabled:opacity-50"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            下一页
          </button>
        </div>
      </footer>
    </div>
  );
}
